import React from 'react';
import { cn } from '../../lib/utils';
import { getInitials } from '../../utils/initials';

export interface AvatarProps {
  /** Size of the avatar */
  size?: number | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Border radius - can use design tokens or custom value */
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | string;
  /** Source URL for the avatar image */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Children to render inside avatar (typically initials) */
  children?: React.ReactNode;
  /** Variant - soft derives a stable Linear-style color from the name, default is neutral muted */
  variant?: 'default' | 'soft';
  /** Name - used to generate initials and, for the soft variant, a stable color */
  name?: string;
  /** Additional CSS class */
  className?: string;
  /** Additional CSS styles */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: () => void;
  /** Forward other HTML div attributes */
  [key: string]: any;
}

export function Avatar({
  size = 'md',
  radius = 'full',
  src,
  alt = 'Avatar',
  children,
  variant = 'default',
  name,
  className = '',
  style = {},
  onClick,
  ...props
}: AvatarProps) {
  // Convert size to pixel value
  const getSizeValue = (size: AvatarProps['size']) => {
    if (typeof size === 'number') {
      return size;
    }

    const sizeMap = {
      'xs': 24,
      'sm': 32,
      'md': 40,
      'lg': 48,
      'xl': 56
    };

    return sizeMap[size as keyof typeof sizeMap] || 40;
  };

  // Initials scale with the avatar's pixel size via an inline style, never a
  // Tailwind `text-N` class: a class-based size silently breaks the day that
  // utility isn't in the build (purge gap, older bundle), leaving initials at the
  // inherited ~16px, which looks huge in a small circle. round(px * 0.25 + 4)
  // reproduces the intended scale (24→10, 32→12, 40→14, 48→16, 56→18) and stays
  // legible for small numeric sizes.
  const getFontSize = (size: AvatarProps['size']): { style: React.CSSProperties } => {
    const px = getSizeValue(size);
    return { style: { fontSize: `${Math.round(px * 0.25 + 4)}px` } };
  };

  // Convert radius to CSS value
  const getRadiusValue = (radius: string) => {
    if (radius === 'none') return '0';
    if (radius === 'full') return '50%';
    if (radius.includes('%') || radius.includes('px')) return radius;

    const radiusMap = {
      'sm': '4px',
      'md': '8px',
      'lg': '12px',
      'xl': '16px'
    };

    return radiusMap[radius as keyof typeof radiusMap] || radius;
  };

  const sizeValue = getSizeValue(size);
  const radiusValue = getRadiusValue(radius);
  const fontSize = getFontSize(size);

  const avatarStyle: React.CSSProperties = {
    width: `${sizeValue}px`,
    height: `${sizeValue}px`,
    borderRadius: radiusValue,
    cursor: onClick ? 'pointer' : 'default',
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...fontSize.style,
    ...style
  };

  // If we have an image source, render img
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={`block object-cover ${className}`.trim()}
        style={avatarStyle}
        onClick={onClick}
        {...props}
      />
    );
  }

  // Handle soft variant (Linear-style: subtle brand colors, balanced visibility)
  if (variant === 'soft' && name) {
    const softColors = [
      { bg: 'bg-indigo-100', text: 'text-indigo-900' },
      { bg: 'bg-blue-100', text: 'text-blue-900' },
      { bg: 'bg-sky-100', text: 'text-sky-900' },
      { bg: 'bg-slate-200', text: 'text-slate-800' },
    ];

    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);

    const colorSet = softColors[Math.abs(hash) % softColors.length];
    const initials = children || getInitials(name);

    return (
      <div
        className={cn(
          'inline-flex items-center justify-center font-medium',
          colorSet.bg,
          colorSet.text,
          className
        )}
        style={avatarStyle}
        onClick={onClick}
        {...props}
      >
        {initials}
      </div>
    );
  }

  // Otherwise render a container with children (initials)
  return (
    <div
      className={cn(
        'inline-flex items-center justify-center bg-muted text-muted-foreground font-medium',
        className
      )}
      style={avatarStyle}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
}
